import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { contactsApi } from '@/lib/api';
import { Plus, Search, Star, Trash2, Users } from 'lucide-react';
import { Button, ConfirmDialog, DataTable, EmptyState, IconButton, Input, Pagination, type Column } from '@elbklar/ui';
import { CrmPageHeader } from '@/components/layout/CrmPageHeader';
import type { Contact } from '@/types';

/** Einträge je Seite. Ausdrücklich gesendet: Der Standard von GET /contacts ist 20, nicht 25. */
const PER_PAGE = 25;

export function ContactsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Bestaetigung als In-App-Dialog statt window.confirm (Trockenlauf 12.10.2026, Befund 9).
  // Der Dialog wartet auf das Loeschen und zeigt einen Fehler, statt still zu schliessen.
  const [zuLoeschen, setZuLoeschen] = useState<Contact | null>(null);
  const handleDelete = (e: React.MouseEvent, contact: Contact) => {
    e.stopPropagation();
    setZuLoeschen(contact);
  };
  const loeschenBestaetigt = async () => {
    if (!zuLoeschen) return;
    await contactsApi.delete(zuLoeschen.id);
    queryClient.invalidateQueries({ queryKey: ['contacts'] });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['contacts', { page, per_page: PER_PAGE, search }],
    queryFn: () => contactsApi.list({ page, per_page: PER_PAGE, search: search || undefined }),
  });

  // Am Handy eine Zeile je Kontakt: Name, darunter Kunde und Position.
  const columns: Column<Contact>[] = [
    {
      key: 'name',
      header: 'Name',
      mobil: 'titel',
      render: (contact) => (
        <span className="flex items-center gap-2">
          <span className="font-medium">
            {contact.first_name} {contact.last_name}
          </span>
          {contact.is_primary && (
            <span title="Primärer Kontakt" className="inline-flex text-highlight">
              <Star size={14} strokeWidth={1.75} aria-label="Primärer Kontakt" />
            </span>
          )}
        </span>
      ),
    },
    { key: 'email', header: 'E-Mail', render: (contact) => contact.email || '–' },
    { key: 'phone', header: 'Telefon', mono: true, render: (contact) => contact.phone || '–' },
    { key: 'role', header: 'Position', mobil: 'unter', render: (contact) => contact.role || '–' },
    {
      key: 'customer_name',
      header: 'Kunde',
      mobil: 'unter',
      render: (contact) => contact.customer_name || contact.partner_name || '–',
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      render: (contact) => (
        <IconButton icon={Trash2} label="Kontakt löschen" variant="ghost" size="sm" onClick={(e) => handleDelete(e, contact)} />
      ),
    },
  ];

  return (
    <div>
      <ConfirmDialog
        open={zuLoeschen !== null}
        onOpenChange={(offen) => !offen && setZuLoeschen(null)}
        title="Kontakt löschen?"
        description={zuLoeschen ? `„${`${zuLoeschen.first_name} ${zuLoeschen.last_name}`.trim()}“ wird endgültig gelöscht.` : ''}
        confirmLabel="Kontakt löschen"
        tone="danger"
        onConfirm={loeschenBestaetigt}
      />

      <CrmPageHeader
        title="Kontakte"
        subtitle={`${data?.total ?? 0} Kontakte insgesamt`}
        actions={
          <Button variant="primary" icon={Plus} onClick={() => navigate('/contacts/new')}>
            Kontakt anlegen
          </Button>
        }
      >
        <div className="w-full pb-4">
          <Input
            icon={Search}
            placeholder="Name oder E-Mail"
            aria-label="Kontakte suchen"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </CrmPageHeader>

      <DataTable<Contact>
        label="Kontakte"
        rows={data?.items ?? []}
        columns={columns}
        loading={isLoading}
        onRowClick={(contact) => navigate(`/contacts/${contact.id}`)}
        empty={<EmptyState icon={Users} title="Keine Kontakte gefunden" className="py-8" />}
      />

      {data && data.total > 0 && (
        <Pagination page={page} pageCount={data.pages} onPageChange={setPage} />
      )}
    </div>
  );
}
